import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { emptyArrayOf } from 'ui-components/default-props/empty';

import { ConfigStepBody } from './config-step/config-step';
import { fieldsTranslations, messages } from './messages';
import { actionMap, initialState, type CustomPackageImportForm } from './reducer';
import { ImportType } from '../../../../../components/csv-import/data/types/import-type';
import { StepType } from '../../../../../components/csv-import/data/types/step-type';
import { ImportProvider } from '../../../../../components/csv-import/ui/components/import-context';
import { SingleImportDrawer } from '../../../../../components/csv-import/ui/components/single-import-drawer/single-import-drawer';
import { ConfigStepFooter } from '../../../../../components/csv-import/ui/components/steps/create-config-step/create-config-step';
import { CreateMappingStepBody } from '../../../../../components/csv-import/ui/components/steps/create-mapping-step/create-mapping-step';
import { CreateMappingStepFooter } from '../../../../../components/csv-import/ui/components/steps/create-mapping-step/create-mapping-step-footer';
import {
  ImportIsProcessingStepBody,
  ImportIsProcessingStepFooter,
} from '../../../../../components/csv-import/ui/components/steps/import-is-processing-step/import-is-processing-step';
import { getTranslationFn } from '../../../../../components/csv-import/ui/utils';
import { PackingRecommendations } from '../../../../../constants/features';
import { useEnableCustomPackageWeights } from '../../../../../feature-flags/hooks/use-enable-custom-package-weights';
import { useIsSellerFeatureEnabled } from '../../../../../hooks/use-is-seller-feature-enabled';
import { CSV_CUSTOM_PACKAGE_IMPORT_JOB_COMPLETE } from '../../../../../services/pusher-service/pusher-contract-messages';
import { Workflows } from '../../../../../tracking/tracking-constants';
import { WorkflowSteps } from '../../../../../tracking/use-workflow-tracking';

const TARE_WEIGHT_ATTRIBUTE = 'tare_weight';
const MAX_WEIGHT_ATTRIBUTE = 'max_weight';
const MAX_WEIGHT_REQUIRED_ATTRIBUTES = [MAX_WEIGHT_ATTRIBUTE];

const steps = [
  {
    id: StepType.CREATE_CONFIG,
    title: <FormattedMessage {...messages.createConfigStep} />,
    body: <ConfigStepBody />,
    footer: <ConfigStepFooter />,
  },
  {
    id: StepType.CREATE_MAPPING,
    title: <FormattedMessage {...messages.createMappingStep} />,
    body: <CreateMappingStepBody />,
    footer: <CreateMappingStepFooter />,
  },
  {
    id: StepType.IMPORT_IS_PROCESSING,
    title: <FormattedMessage {...messages.importResult} />,
    body: (
      <ImportIsProcessingStepBody
        successMessage={<FormattedMessage {...messages.importResultSuccess} />}
        title={<FormattedMessage {...messages.importResult} />}
      />
    ),
    footer: <ImportIsProcessingStepFooter />,
  },
];

const translateField = getTranslationFn(fieldsTranslations, ImportType.customPackage);

type CustomPackageImportDrawerProps = {
  onClose: () => void;
  onSuccess: () => void;
  trackProcess: (workflowStep: string, metadata?: Record<string, unknown>) => void;
};

export const CustomPackageImportDrawer = ({ onClose, onSuccess, trackProcess }: CustomPackageImportDrawerProps) => {
  const isWeightsEnabled = useEnableCustomPackageWeights();
  const isPackingRecommendationsEnabled = useIsSellerFeatureEnabled(PackingRecommendations);
  const shouldShowMaxWeight = isWeightsEnabled || isPackingRecommendationsEnabled;

  // Mirrors the Add/Edit Custom Package modal and the custom packages grid: tare weight only shows behind
  // isWeightsEnabled, while max weight shows behind either flag.
  const attributesToHide = useMemo(() => {
    const hiddenAttributes: string[] = [];
    if (!isWeightsEnabled) {
      hiddenAttributes.push(TARE_WEIGHT_ATTRIBUTE);
    }
    if (!shouldShowMaxWeight) {
      hiddenAttributes.push(MAX_WEIGHT_ATTRIBUTE);
    }
    return hiddenAttributes.length > 0 ? hiddenAttributes : emptyArrayOf<string>();
  }, [isWeightsEnabled, shouldShowMaxWeight]);

  const createCustomImportSettings = useCallback(
    (form: object) => ({
      overwriteExisting: (form as CustomPackageImportForm).shouldOverwriteExisting,
      isMaxWeightRequired: shouldShowMaxWeight,
    }),
    [shouldShowMaxWeight],
  );

  const onCancel = (step: string) => () => {
    onClose();
    trackProcess(WorkflowSteps.CANCEL, { step });
  };

  return (
    <ImportProvider
      actionMap={actionMap}
      attributesToHide={attributesToHide}
      attributesToRequire={shouldShowMaxWeight ? MAX_WEIGHT_REQUIRED_ATTRIBUTES : emptyArrayOf<string>()}
      createCustomImportSettings={createCustomImportSettings}
      initialState={initialState}
      importType={ImportType.customPackage}
      onClose={onCancel(StepType.IMPORT_IS_PROCESSING)}
      onComplete={onSuccess}
      pusherEvent={CSV_CUSTOM_PACKAGE_IMPORT_JOB_COMPLETE}
      trackProcess={trackProcess}
      translateField={translateField}
      workflowType={Workflows.CSV_CUSTOM_PACKAGE_IMPORT_FLOW}
    >
      <SingleImportDrawer header={<FormattedMessage {...messages.header} />} onCancel={onCancel} steps={steps} />
    </ImportProvider>
  );
};
